import { Box, Typography, TextField, MenuItem, Select, InputLabel, FormControl, Switch, Tooltip, Button } from "@mui/material";
import { useEffect, useState } from "react";
import IEslTemplate from "../../../models/esl/eslTemplate";
import { useTranslation } from "react-i18next";
import IAdminPageBuildingDetail from "../../../models/adminPage/adminPageBuildingDetail";
import BuildingService from "../../../services/building/BuildingService";

interface ITemplateInfoProps {
  template: IEslTemplate;
  setTemplate: (template: IEslTemplate) => void;
}

const TemplateInfo = ({ template, setTemplate }: ITemplateInfoProps) => {
  const { t } = useTranslation(['eslTemplate', 'common']);
  const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);

  useEffect(() => {
    BuildingService.GetBuildingsDetails().then((setBuildings));
  }, []);

  const setTemplateBuildings = (selectedBuildingIds: number[]) => {
    const removedTemplateBuildings = template.buildings.filter((b) => !selectedBuildingIds.includes(b.buildingId));
    const newBuildingIds = selectedBuildingIds.filter((id) => !template.buildings.some((b) => b.buildingId === id));

    const newTemplateBuildings = newBuildingIds.map((id) => ({ id: 0, buildingId: id, eslTemplateId: template.id, isActive: false }));
    const updatedTemplateBuildings = [...template.buildings.filter((b) => !removedTemplateBuildings.includes(b)), ...newTemplateBuildings];

    setTemplate({ ...template, buildings: updatedTemplateBuildings });
  };

  const onSetTemplateBuildingActive = (buildingId: number) => {
    const updatedTemplateBuildings = template.buildings.map((b) => b.buildingId === buildingId ? { ...b, isActive: !b.isActive } : b);
    setTemplate({ ...template, buildings: updatedTemplateBuildings });
  };

  const onActivateAll = () => {
    const updatedTemplateBuildings = template.buildings.map((b) => ({ ...b, isActive: true }));
    setTemplate({ ...template, buildings: updatedTemplateBuildings });
  };

  const onDisableAll = () => {
    const updatedTemplateBuildings = template.buildings.map((b) => ({ ...b, isActive: false }));
    setTemplate({ ...template, buildings: updatedTemplateBuildings });
  };

  return (
    <Box>
      <Typography variant="h6" mb={2}>{t('eslTemplate:templateEditor.templateInfo.title')} </Typography>
      <TextField
        label={t('eslTemplate:createModal.name')}
        value={template.name}
        onChange={(e) => setTemplate({ ...template, name: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth>
        <InputLabel id="buildings-select-input">{t('eslTemplate:createModal.buildings')}</InputLabel>
        <Select
          label={t('eslTemplate:createModal.buildings')}
          labelId="buildings-select-input"
          multiple
          value={template.buildings.map((b) => b.buildingId)}
          onChange={(e) => setTemplateBuildings(e.target.value as number[])}
          sx={{ width: '100%', height: '2em' }}
          MenuProps={{ style: { overflowY: 'visible' } }}
        >
          {buildings.map((building) => (
            <MenuItem
              key={building.id}
              value={building.id}
            >
              {building.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        marginTop: 2,
      }}>
        {template.buildings.sort((a, b) => a.buildingId - b.buildingId).map((templateBuilding) => (
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F0F0F0',
            paddingInline: 2,
            paddingBlock: 1,
            borderRadius: 1,
          }}>
            <Typography>
              {buildings.find((building) => building.id === templateBuilding.buildingId)?.name}
            </Typography>

            <Tooltip title={t("eslTemplate:activeSwitchTooltip")} placement='top' arrow>
              <Switch checked={templateBuilding.isActive} onChange={() => onSetTemplateBuildingActive(templateBuilding.buildingId)} size='small' />
            </Tooltip>
          </Box>
        ))}
      </Box>


      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        marginTop: 2,
      }}>
        <Button variant="contained" color="primary" size="small" sx={{ width: "100%" }}
          onClick={() => onActivateAll()}>
          {t('eslTemplate:templateEditor.templateInfo.activateAll')}
        </Button>

        <Button variant="contained" color="inherit" size="small" sx={{ width: "100%" }}
          onClick={() => onDisableAll()}>
          {t('eslTemplate:templateEditor.templateInfo.disableAll')}
        </Button>
      </Box>
    </Box>
  );
}

export default TemplateInfo;